<template>
  <div class="card border-0 shadow-none rounded-1 mb-25">
    <div class="card-body p-xl-40">
      <a-table
        :columns="columns"
        :data-source="transfers"
        :row-key="'id'"
        :loading="loading"
        :pagination="paginationConfig"
        :locale="tableLocale"
        :scroll="{ x: 1100 }"
        class="text-nowrap align-middle transfer-table"
      >
        <template #bodyCell="{ column, record }">
          <template v-if="column && column.key === 'date'">
            {{ formatDate(record.created_at) }}
          </template>

          <template v-else-if="column && column.key === 'from_warehouse'">
            {{ record.from_warehouse?.name || record.from_wh_name || t("transfer_page.not_available_short") }}
          </template>

          <template v-else-if="column && column.key === 'to_warehouse'">
            {{ record.to_warehouse?.name || record.to_wh_name || t("transfer_page.not_available_short") }}
          </template>

          <template v-else-if="column && column.key === 'items_count'">
            <span class="badge bg-light text-dark item-count-badge">
              {{ t("transfer_page.items_count", { count: record.items?.length || 0 }) }}
            </span>
          </template>

          <template v-else-if="column && column.key === 'grand_total'">
            ${{ Number(record.grand_total || 0).toFixed(2) }}
          </template>

          <template v-else-if="column && column.key === 'status'">
            <span class="status-badge" :class="`status-${String(record.status || 'pending').toLowerCase()}`">
              {{ formatStatus(record.status) }}
            </span>
          </template>

          <template v-else-if="column && column.key === 'actions'">
            <div class="d-flex justify-content-center gap-2 table-actions">
              <button
                class="btn p-0 border-0 action-icon-btn"
                :title="t('transfer_page.view')"
                data-bs-toggle="modal"
                data-bs-target="#detailsModal"
                @click="$emit('view-transfer', record)"
              >
                <img src="../../../assets/img/icons/eye.svg" :alt="t('transfer_page.view')" style="width: 14px" />
              </button>
              <router-link :to="`/edit-transfer/${record.id}`" :title="t('transfer_page.edit')" class="btn p-0 border-0 action-icon-btn">
                <img src="../../../assets/img/icons/edit.svg" :alt="t('transfer_page.edit')" style="width: 14px" />
              </router-link>
              <button
                class="btn p-0 border-0 action-icon-btn"
                :title="t('transfer_page.delete')"
                @click="$emit('delete-transfer', record)"
              >
                <img src="../../../assets/img/icons/close.svg" :alt="t('transfer_page.delete')" style="width: 14px" />
              </button>
            </div>
          </template>
        </template>
      </a-table>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent } from "vue";
import { useI18n } from "vue-i18n";
import type { Transfer } from "../../../services/transfer";

export default defineComponent({
  name: "TransferList",
  props: {
    transfers: {
      type: Array as () => Transfer[],
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["view-transfer", "delete-transfer"],
  setup() {
    const { t, locale } = useI18n();
    const tableLocale = computed(() => ({
      emptyText: t("transfer_page.no_transfers_found"),
    }));

    const columns = computed(() => [
      { title: t("transfer_page.date"), dataIndex: "created_at", key: "date", align: "center" as const, width: 210 },
      { title: t("transfer_page.reference"), dataIndex: "ref", key: "ref", align: "center" as const, width: 150 },
      { title: t("transfer_page.from_warehouse"), key: "from_warehouse", align: "center" as const, width: 180 },
      { title: t("transfer_page.to_warehouse"), key: "to_warehouse", align: "center" as const, width: 180 },
      { title: t("transfer_page.total_items"), key: "items_count", align: "center" as const, width: 140 },
      { title: t("transfer_page.grand_total"), key: "grand_total", align: "center" as const, width: 140 },
      { title: t("transfer_page.status"), key: "status", align: "center" as const, width: 140 },
      { title: t("transfer_page.action"), key: "actions", align: "center" as const, width: 120 },
    ]);
    const paginationConfig = computed(() => ({
      showSizeChanger: true,
      pageSizeOptions: ["10", "20", "30", "50", "100"],
      defaultPageSize: 10,
      locale: { items_per_page: t("transfer_page.items_per_page") },
    }));

    const formatDate = (dateString?: string) => {
      if (!dateString) return t("transfer_page.not_available");
      return new Date(dateString).toLocaleString(locale.value === "km" ? "km-KH" : "en-GB", {
        timeZone: "Asia/Phnom_Penh",
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: true,
      });
    };

    const formatStatus = (status?: string) => {
      const value = String(status || "pending").toLowerCase();
      return t(`transfer_page.status_${value}`);
    };

    return {
      columns,
      paginationConfig,
      tableLocale,
      formatDate,
      formatStatus,
      t,
    };
  },
});
</script>

<style scoped>
.transfer-table :deep(.ant-table-cell) {
  vertical-align: middle;
}

.transfer-table :deep(.ant-table-thead > tr > th) {
  white-space: nowrap;
}

.item-count-badge {
  font-size: 0.8rem;
  font-weight: 600;
  padding: 0.5rem 0.7rem;
}

.table-actions {
  min-width: 72px;
}

.action-icon-btn {
  width: 24px;
  height: 24px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 90px;
  padding: 6px 12px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
}

.status-completed {
  color: #1f9254;
  background: #e8f7ee;
}

.status-pending {
  color: #d33c43;
  background: #fdecec;
}

.status-sent {
  color: #3563e9;
  background: #edf4ff;
}
</style>
